import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CircularProgress from "@mui/material/CircularProgress";

import "./ImportReport.scss";

import {
	AbsDropDown,
	AttachmentsUpload,
	Button,
	COLORS,
	Table,
	Modal
} from "abs-ui-components";

import {
	importReportDownloadTemplate,
	importReportTemplateInstructions,
	importErpReportTemplateInstructions,
	importErpReportDownloadTemplate,
	importFuelEUReportDownloadTemplate,
	importFuelEUReportTemplateInstructions,
	importAdvantageUnifiedReportDownloadTemplate,
	importAdvantageUnifiedReportTemplateInstructions
} from "../../constants/import_constants";
import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import xlsxImage from "../../images/excel-file-icon.svg";
import { FeatureType } from "../../constants/FeatureToggles";
import { ClaimsContext } from "../../services/ClaimsService";
import { set } from "lodash";
import webhookQueueService from "../../services/WebhookQueueService";

const ImportReport = (props) => {
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [invalidFiles, setInvalidFiles] = useState([]);
	const [invalidVoyages, setInvalidVoyages] = useState([]);
	const [invalidBDNs, setInvalidBDNs] = useState([]);
	const [invalidFuelDetails, setInvalidFuelDetails] = useState([]);
	const [uploadingFile, setUploadingFile] = useState(false);
	const [selectedWcn, setSelectedWcn] = useState([]);
	const [wcnOptionList, setWcnOptionList] = useState([]);
	const [uploadResponse, setUploadResponse] = useState([]);
	const ssoUserEmail = localStorage.getItem("userEmail");
	const [dragged, setDragged] = useState(null);
	const [nonMeFileName, setnonMeFileName] = useState([]);
	const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
	const [confirmProceedDownload, setConfirmProceedDownload] = useState(false);	
	const [disableProceedToDownload, setDisableProceedToDownload] = useState(false);	
	const [currentOperationId, setCurrentOperationId] = useState(null);
	const currentOperationIdRef = useRef(null);
	const [webhookPrompt, setWebhookPrompt] = useState(null);

	useEffect(() => {
		cancelFileSelection();

		// Re-attach callbacks for any in-progress Import Report after page refresh
		const state = webhookQueueService.getState();
		const inProgressOp = state.queue.find(
			(op) => op.label === "Import Report" && op.status === "processing"
		);
		if (inProgressOp?.webhookId) {
			setCurrentOperationId(inProgressOp.id);
			currentOperationIdRef.current = inProgressOp.id;
			const imoNumberMatch = inProgressOp.description?.match(/IMO\s+(\S+)/);
			const imoNumber = imoNumberMatch ? imoNumberMatch[1] : "";
			webhookQueueService.reattachCallbacks(inProgressOp.webhookId, {
				onComplete: (result) => {
					props.setDisableBtn(false);

					if (result?.isSuccess === false || result?.IsSuccess === false) {
						const errorMsg =
							result?.Data?.ErrorMessage ||
							result?.data?.errorMessage ||
							"Error while importing reports. Please try again";

						setInvalidFiles([
							{
								...result,
								fileName: result?.Data?.FileName || result?.data?.fileName || "Imported File",
								message: errorMsg,
								errorLogBase64:
									result?.Data?.ErrorLogBase64 ??
									result?.data?.errorLogBase64 ??
									result?.errorLogBase64,
								errorLogFileName:
									result?.Data?.ErrorLogFileName ??
									result?.data?.errorLogFileName ??
									result?.errorLogFileName,
							},
						]);
						setUploadingFile(false);
					} else if (result?.IsSuccess) {
						const reportId = result?.Data?.ReportId || result?.data?.reportId;
						if (reportId) {
							const baseUrl = window.location.origin;
							result._reportUrl = `${baseUrl}/reports/${reportId}`;
						}
						const successMsg = imoNumber
							? `IMO ${imoNumber} - Imported Successfully`
							: "Imported Successfully";
						emitToasterMsg("success", "Import Report", successMsg);
						props.onImportSuccess?.();
						setUploadingFile(false);
						cancelFileSelection();
						props.cancelImportReport(false);
					} else {
						setUploadingFile(false);
						cancelFileSelection();
						props.cancelImportReport(false);
					}
				},
				onError: (error, statusData) => {
					setCurrentOperationId(null);
					currentOperationIdRef.current = null;
					props.setDisableBtn(false);
					
					const result = statusData?.result;
					const errorMsg = 
						result?.Data?.ErrorMessage ||
						result?.data?.errorMessage ||
						result?.ErrorMessage ||
						(typeof error === "string" ? error : error?.message) ||
						"Error while importing reports. Please try again";

					setInvalidFiles([
						{
							fileName: result?.Data?.FileName || result?.data?.fileName || "Imported File",
							message: errorMsg,
							errorLogBase64:
								result?.Data?.ErrorLogBase64 ??
								result?.data?.errorLogBase64 ??
								null,
							errorLogFileName:
								result?.Data?.ErrorLogFileName ??
								result?.data?.errorLogFileName ??
								null,
						},
					]);
					setUploadingFile(false);
				},
			});
		}

		return () => {
			setSelectedFiles([]);
			setInvalidFiles([]);
			setInvalidBDNs([]);
			setInvalidFuelDetails([]);
			setInvalidVoyages([]);
			setSelectedWcn([]);
			setWcnOptionList([]);
			setUploadingFile(false);
			setUploadResponse([]);
			setnonMeFileName([]);
		};
	}, []);

	useEffect(() => {
		const unsubscribe = webhookQueueService.subscribe((state) => {
			const waitingOp = state.waitingForInput?.[0];
			if (waitingOp?.prompt) {
				setWebhookPrompt({ operationId: waitingOp.id, ...waitingOp.prompt });
			} else {
				setWebhookPrompt(null);
			}
		});
		return () => unsubscribe();
	}, []);

	// Cancel silent webhook operation when the modal is unmounted
	useEffect(() => {
		return () => {
			const opId = currentOperationIdRef.current;
			if (opId) {
				const isWebhookPanelVisible =
					userFeaturesAreLoaded && userHasFeature(FeatureType.ViewWebhookPanel);
				if (!isWebhookPanelVisible) {
					webhookQueueService.cancelOperation(opId);
					props.setDisableBtn(false);
				}
			}
		};
	}, [userFeaturesAreLoaded]);

	const invalidFilesColumns = useMemo(
		() => [
			{
				Header: "File Name",
				accessor: "fileName",
			},
			{
				Header: "Message",
				accessor: "message",
				Cell: (cellData) => {
					const rowData = cellData?.row;
					const errorLogBase64 =
						rowData?.errorLogBase64 ??
						rowData?.data?.errorLogBase64;
					const errorLogFileName =
						rowData?.errorLogFileName ??
						rowData?.data?.errorLogFileName;
					const message = rowData?.message ?? "";
					return (
						<div className="d-flex flex-column">
							<span
								className="text-danger"
								dangerouslySetInnerHTML={{
									__html: message.replace(
										/<a\s(.*?)>(.*?)<\/a>/g,
										'<a $1 style="color: blue; text-decoration: underline; cursor: pointer;">$2</a>'
									),
								}}
							/>
							{errorLogBase64 && (
								<Button
									type="button"
									className="mt-2 select-file-color cancel-excel download-errors-btn"
									primaryBackgroundColor="#f9f9f9"
									primaryColor="#f9f9f9"
									onClick={() =>
										handleDownloadErrorLog(
											errorLogBase64,
											errorLogFileName
										)
									}
								>
									Download Errors
								</Button>
							)}
						</div>
					);
				},
			},
		],
		[]
	);

	const onDrag = (e) => {
		e.preventDefault();
		if (e.type === "dragover") {
			setDragged(true);
		}
		setDragged((prev) => {
			return prev;
		});
	};

	const onDrop = (e) => {
		e.preventDefault();
		if (e.type === "drop") {
			setDragged(false);
		}
		setDragged((prev) => {
			return prev;
		});
	};

	const handleTemplateInstructions = () => {
		const link = document.createElement("a");
		link.href = importReportTemplateInstructions;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleDownloadTemplate = () => {
		const link = document.createElement("a");
		link.href = importReportDownloadTemplate;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleErpTemplateInstructions = () => {
		const link = document.createElement("a");
		link.href = importErpReportTemplateInstructions;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleErpDownloadTemplate = () => {
		setConfirmProceedDownload(true);
	};

	const proceedToDownload = async () => {
		setDisableProceedToDownload(true);
		try {
			const response = await fetch(importErpReportDownloadTemplate);
			if (!response.ok) {
				throw new Error("Failed to download file");
			}
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = "templates_1.1_ERP7_import_sheet.xlsx"; 
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Download failed:", error);
		} finally {
			setDisableProceedToDownload(false);
		}
	};
	
	const handleFueEUDownloadTemplate = () => {
		const link = document.createElement("a");
		link.href = importFuelEUReportDownloadTemplate;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleFuelEUTemplateInstructions = () => {
		const link = document.createElement("a");
		link.href = importFuelEUReportTemplateInstructions;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	function handleDownloadErrorLog(base64Data, fileName) {
		if (!base64Data) return;
		try {
			const byteCharacters = window.atob(base64Data);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			const blob = new Blob([byteArray], { type: "text/plain" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download =
				fileName || "AdvantageUnifiedTemplateErrors.txt";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error log download failed:", error);
		}
	}

	const cancelFileSelection = () => {
		setSelectedFiles([]);
		setInvalidFiles([]);
		setInvalidBDNs([]);
		setInvalidFuelDetails([]);
		setInvalidVoyages([]);
		setSelectedWcn([]);
		setWcnOptionList([]);
		setUploadingFile(false);
	};

	const handleRemoveFile = (indexToRemove) => {
		setSelectedFiles(
			selectedFiles.filter((file, index) => index !== indexToRemove)
		);
		setSelectedWcn(
			selectedWcn.filter((option, index) => index !== indexToRemove)
		);
		setWcnOptionList(
			wcnOptionList.filter((option, index) => index !== indexToRemove)
		);
		setUploadResponse(
			uploadResponse?.response?.data?.filter(
				(option, index) => index !== indexToRemove
			)
		);
		if (selectedFiles.length === 1) {
			cancelFileSelection();
			setInvalidFiles([]);
		}
	};

	const handleFilesSelected = (e) => {
		const newFiles = [...selectedFiles];
		const newSelectedWcn = [...selectedWcn];
		e?.forEach((selectedFile) => {
			newFiles.push(selectedFile.file);
			newSelectedWcn.push("");
		});
		setSelectedFiles(newFiles);

		setSelectedWcn(newSelectedWcn);
	};

	const createWcnOptions = (data) => {
		let wcnOptionList = [];
		data.wcnMsts?.forEach((item) => {
			const wcnOption = [];

			if (item.wcnNumber && item.companyName) {
				wcnOptionList.push({
					label: `${item.wcnNumber} - ${item.companyName}`,
					value: item.wcnMstId,
				});
			}
		});

		setWcnOptionList(wcnOptionList);
	};

	const uploadFiles = () => {
		setUploadingFile(true);
		const formData = new FormData();
		const userEmail = localStorage.getItem("userEmail");
		var userName = userEmail || ssoUserEmail;
		selectedFiles.forEach((file) => {
			formData.append("file", file);
		});
		axiosHttp
			.post(
				`api/Report/ImportReportFile?reportYear=${
					props.reportingYear || 2024
				}`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			)
			.then((response) => {
				if (response.data) {
					setnonMeFileName(response.data.fileName);
					if (response.data.isFileValid === false) {
						setInvalidFiles([
							{
								...response.data,
								message: response.data.errorMessage,
							},
						]);
					} else if (response.data.isFileValid === true) {
						setInvalidFiles([]);
						setUploadResponse([{ ...response.data }]);
						createWcnOptions(response.data);
						const invalidRes = getInvalidDataFromResponse(
							response.data
						);
						setInvalidVoyages(invalidRes?.invalidVoyagesList);
						setInvalidBDNs(invalidRes?.invalidBDNsList);
						setInvalidFuelDetails(
							invalidRes?.invalidFuelDetailsList
						);
					}
				}
				setUploadingFile(false);
			})
			.catch((error) => {
				setUploadingFile(false);
				emitToasterMsg(
					"error",
					"Import Reports",
					error?.data?.errorMessage ||
						"Error while uploading reports. Please try again"
				);
			});
	};

	const createSaveFileDataPayload = () => {
		setUploadingFile(true);
		props.setDisableBtn(true);
		const requestSaveFilePayload = uploadResponse?.map((item, index) => {
			delete item["wcnMsts"];
			item["wcnMstId"] = selectedWcn[index];
			return item;
		});

		if (wcnOptionList.length > 0) {
			saveFileData(requestSaveFilePayload);
		} else {
			setUploadingFile(false);
		}
	};

	const saveFileData = (dataToSend) => {
        const imoNumber = dataToSend[0]?.imoNumber || dataToSend[0]?.ImoNumber || dataToSend[0]?.imo || uploadResponse?.[0]?.imoNumber || uploadResponse?.[0]?.ImoNumber || "";

        webhookQueueService.enqueue(
            () =>
                axiosHttp.post(
                    `api/Report/ConfirmImportReport?reportYear=${
                        props.reportingYear || 2024
                    }`,
                    dataToSend[0]
                ),
            {
                label: imoNumber
                    ? `Import Report - IMO ${imoNumber}`
                    : "Import Report",
                description: imoNumber
                    ? `Importing report data for IMO ${imoNumber}`
                    : "Importing report data",
                onComplete: (result) => {
                    setCurrentOperationId(null);
                    currentOperationIdRef.current = null;
                    props.setDisableBtn(false);

                    if (result?.isSuccess === false || result?.IsSuccess === false) {
                        const errorMsg =
                            result?.Data?.ErrorMessage ||
                            result?.data?.errorMessage ||
                            "Error while importing reports. Please try again";

                        setInvalidFiles([
                            {
                                ...result,
                                fileName: nonMeFileName,
                                message: errorMsg,
                                errorLogBase64:
                                    result?.Data?.ErrorLogBase64 ??
                                    result?.data?.errorLogBase64 ??
                                    result?.errorLogBase64,
                                errorLogFileName:
                                    result?.Data?.ErrorLogFileName ??
                                    result?.data?.errorLogFileName ??
                                    result?.errorLogFileName,
                            },
                        ]);
                        setUploadingFile(false);
                    } else if (result?.IsSuccess) {
                        const reportId = result?.Data?.ReportId || result?.data?.reportId;
                        if (reportId) {
                            const baseUrl = window.location.origin;
                            result._reportUrl = `${baseUrl}/reports/${reportId}`;
                        }
						const successMsg = imoNumber
							? `IMO ${imoNumber} - Imported Successfully`
							: "Imported Successfully";
						emitToasterMsg("success", "Import Report", successMsg);
                        props.onImportSuccess?.();
                        setUploadingFile(false);
                        cancelFileSelection();
                        props.cancelImportReport(false);
                    } else {
                        setUploadingFile(false);
                        cancelFileSelection();
                        props.cancelImportReport(false);
                    }
                },
                onError: (error, statusData) => {
                    props.setDisableBtn(false);
                    
                    const result = statusData?.result;
                    const errorMsg = 
                        result?.Data?.ErrorMessage ||
                        result?.data?.errorMessage ||
                        result?.ErrorMessage ||
                        (typeof error === "string" ? error : error?.message) ||
                        "Error while importing reports. Please try again";

                    setInvalidFiles([
                        {
                            fileName: result?.Data?.FileName || result?.data?.fileName || "Imported File",
                            message: errorMsg,
                            errorLogBase64:
                                result?.Data?.ErrorLogBase64 ??
                                result?.data?.errorLogBase64 ??
                                null,
                            errorLogFileName:
                                result?.Data?.ErrorLogFileName ??
                                result?.data?.errorLogFileName ??
                                null,
                        },
                    ]);
                    setUploadingFile(false);
                },
            }
        ).then((operationId) => {
            setCurrentOperationId(operationId);
            currentOperationIdRef.current = operationId;
        });

        // cancelFileSelection();
    };

	const getInvalidDataFromResponse = (response) => {
		let invalidVoyagesList = [];
		let invalidBDNsList = [];
		let invalidFuelDetailsList = [];
		const voyages = response.voyageDtlReqs;
		const bdns = response.voyageBdnDtlReqs;
		const fuels = response.reportOtherFuelsDtlReqs;
		voyages?.forEach((e) => {
			invalidVoyagesList.push({
				fileName: response.fileName,
				message: e.errorMessage,
			});
		});

		bdns?.forEach((e) => {
			invalidBDNsList.push({
				fileName: response.fileName,
				message: e.errorMessage,
			});
		});

		fuels?.forEach((e) => {
			invalidFuelDetailsList.push({
				fileName: response.fileName,
				message: e.errorMessage,
			});
		});
		return { invalidVoyagesList, invalidBDNsList, invalidFuelDetailsList };
	};

	const handleSelectedWcnChange = (index, e) => {
		let updatedOptions = selectedWcn.map((option, i) => {
			return i === index ? e.value : option;
		});
		setSelectedWcn(updatedOptions);
	};

	const handlePromptResponse = async (confirmed) => {
		if (!webhookPrompt?.operationId) return;
		await webhookQueueService.respondToPrompt(webhookPrompt.operationId, confirmed);
		setWebhookPrompt(null);
	};

	const onDraggingEnd = (e) => {
		setDragged(false);
	};

    return (
        <>
            {webhookPrompt &&
                createPortal(
                    <div className="confirm-backdrop" />,
                    document.body
                )}
            {webhookPrompt &&
                createPortal(
                    <div className="confirm-modal-layer">
                        <Modal
                            isOpen={!!webhookPrompt}
                            padding={"70px"}
                            headerHeight={"50px"}
                            showFooter
                            onCloseClickListener={() => handlePromptResponse(false)}
                            footer={
                                <div className="d-flex justify-content-end fixed-action-btns p-4">
                                    <Button
                                        className="modal-delete-btn"
                                        onClick={() => handlePromptResponse(true)}
                                        primaryColor={COLORS.RED.DARK}
                                        primaryBackgroundColor={COLORS.RED.LIGHT}
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        className="modal-cancel-btn ml-2"
                                        onClick={() => handlePromptResponse(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            }
                        >
                            <div>{webhookPrompt?.Message || "The server requires your confirmation to proceed."}</div>
                        </Modal>
                    </div>,
                    document.body
                )}
            {confirmProceedDownload &&
                createPortal(
                    <div className="confirm-backdrop" />,
                    document.body
                )}
            {confirmProceedDownload &&
                createPortal(
                    <div className="confirm-modal-layer">
                        <Modal
                            isOpen={confirmProceedDownload}
                            padding={"70px"}
                            headerHeight={"50px"}
                            showFooter
                            onCloseClickListener={() => {
                                setConfirmProceedDownload(false);
                            }}
                            footer={
                                <div className="d-flex justify-content-end fixed-action-btns p-4">
                                    <Button
                                        className="modal-delete-btn"
                                        onClick={() => proceedToDownload()}
                                        primaryColor={COLORS.RED.DARK}
                                        primaryBackgroundColor={COLORS.RED.LIGHT}
                                        disabled={disableProceedToDownload}
                                    >
                                        Proceed to Download
                                    </Button>
                                    <Button
                                        className="modal-cancel-btn ml-2"
                                        onClick={() => {
                                            setConfirmProceedDownload(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            }
                        >
                            <div>Note: The template v.1.1 should not be used for the reporting periods after 31 December 2025.</div>
                        </Modal>
                    </div>,
                    document.body
                )}
            <div
			className="import-report-container"
			onDragLeave={(e) => onDraggingEnd(e)}>
			{/* <div className="d-flex justify-content-center">
				<Button onClick={handleErpDownloadTemplate}>
					Download Template v1.1
				</Button>
				<Button
					variant="secondary"
					className="ml-2"
					onClick={handleErpTemplateInstructions}
				>
					Templates Guide v1.1
				</Button>
			</div> */}
			{/* <div className="d-flex justify-content-center mt-2">
				<Button onClick={handleDownloadTemplate}>
					Download Template v2.0
				</Button>
				<Button
					variant="secondary"
					className="ml-2"
					onClick={handleTemplateInstructions}
				>
					Template Instructions v2.0
				</Button>
			</div> */}
			<div className="d-flex justify-content-center mt-2">
				<Button onClick={handleFueEUDownloadTemplate}>
					Download Template v2.2.2
				</Button>
				<Button
					variant="secondary"
					className="ml-2"
					onClick={handleFuelEUTemplateInstructions}
				>
					Templates Guide v2.2.2
				</Button>
			</div>

			<div className="p-4">
				<div
					onDragOver={(e) => onDrag(e)}
					className={dragged ? "drag-drop-cls" : null}
					onDrop={(e) => onDrop(e)}
				>
					<AttachmentsUpload
						onFilesSelected={handleFilesSelected}
						supportedFormat="excel"
						supportedFileTypes="xlsx, xls"
					/>
				</div>

				<div className="selected-content mt-4">
					{selectedFiles && selectedFiles.length > 0 && (
						<>
							<div className="file-info p-3">
								{selectedFiles.map((file, index) => (
									<div
										key={`import-${index}`}
										className="d-flex justify-content-between align-items-center px-3 py-1"
									>
										<div className="d-flex">
											<img
												src={xlsxImage}
												alt="Excel File Icon"
												className="xl-img mt-1"
											/>
											<span className="file-name p-2">
												{" "}
												{file.name}{" "}
											</span>
										</div>
										<div className="file-info-actions">
											{wcnOptionList.length > 0 && (
												<AbsDropDown
													options={wcnOptionList}
													value={selectedWcn[index]}
													selected={
														selectedWcn[index]
													}
													handleChange={(e) =>
														handleSelectedWcnChange(
															index,
															e
														)
													}
													placeholder="Please Select WCN"
												/>
											)}
											<button
												type="button"
												className="close p-0"
												aria-label="Close"
												onClick={() =>
													handleRemoveFile(index)
												}
												disabled={uploadingFile}
											>
												<span aria-hidden="true">
													{" "}
													×{" "}
												</span>
											</button>
										</div>
									</div>
								))}
							</div>
							<div className="mt-4 d-flex justify-content-end upload-container items-center">
								{uploadingFile && (
									<CircularProgress size={30} />
								)}
								{wcnOptionList?.length > 0 ? (
									<Button
										primaryBackgroundColor="#DC1E34"
										primaryColor="#DC1E34"
										className="p-0 upload-excel ml-4"
										onClick={createSaveFileDataPayload}
										disabled={
											props.disableBtn ||
											selectedWcn.some((e) => e === "")
										}
									>
										Proceed
									</Button>
								) : (
									<Button
										primaryBackgroundColor="#DC1E34"
										primaryColor="#DC1E34"
										className="p-0 upload-excel ml-4"
										onClick={uploadFiles}
										disabled={uploadingFile}
									>
										Upload
									</Button>
								)}
								<Button
									onClick={cancelFileSelection}
									className="ml-2 select-file-color cancel-excel"
									primaryBackgroundColor="#f9f9f9"
									primaryColor="#f9f9f9"
									disabled={uploadingFile}
								>
									Clear
								</Button>
							</div>
						</>
					)}
				</div>

				{invalidFiles?.length > 0 && (
					<>
						<div className="mt-3">
							One or More errors found with files as below :
						</div>
						<Table
							columns={invalidFilesColumns}
							data={invalidFiles}
						/>
					</>
				)}
			</div>
			</div>
		</>
	);
};

export default ImportReport;
